import { forwardRef, useImperativeHandle, useState, useCallback, useEffect, memo } from 'react';
import { OfferInputData } from '../../../../hooks/Marketing/useGetLatestAnimations';
import {
  Box,
  Grid,
  Typography,
  Button,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useLayoutPreferences } from '../../../../context/LayoutPreferencesContext';

const DISCOUNT_TYPE_OPTIONS = [
  { id: 1, label: '% ou € sur le montant\ntotal de la transaction' },
  { id: 2, label: 'Mouvements de\npoints' },
  { id: 3, label: "Calcul d'un % sur le montant\ntotal de la dernière transaction" },
] as const;

const TRIGGER_OPTIONS = [
  { id: 'montant',     label: 'Par un montant de\ntransaction dépassé' },
  { id: 'article',     label: "Par un nombre\nd'article" },
  { id: 'points',      label: 'Par un seuil de\npoint dépassé' },
  { id: 'passage',     label: 'Au prochain\npassage en caisse' },
  { id: 'action',      label: "Par une action client\n(Offre à la demande)" },
  { id: 'inscription', label: "Par l'inscription au\nprogramme de fid" },
] as const;

const PREREQ_OPTIONS = [
  { id: 'montant', label: 'Par un montant de\ntransaction dépassé' },
  { id: 'article', label: "Par un nombre\nd'article" },
  { id: 'points',  label: 'Par un seuil de\npoint dépassé' },
] as const;

const TRIGGER_ID_MAP: Record<string, number> = {
  montant: 1, article: 2, points: 3, passage: 4, action: 5, inscription: 6,
};

const PREREQ_ID_MAP: Record<string, number> = {
  montant: 1, article: 2, points: 3,
};

const TRIGGER_KEY_MAP: Record<number, string> = Object.fromEntries(
  Object.entries(TRIGGER_ID_MAP).map(([k, v]) => [v, k]),
);

const PREREQ_KEY_MAP: Record<number, string> = Object.fromEntries(
  Object.entries(PREREQ_ID_MAP).map(([k, v]) => [v, k]),
);

export const offerInputToForm = (o: OfferInputData): OfferFormData => {
  const pd = (o.payloadRefDiscountType ?? {}) as Record<string, string>;
  const pe = (o.payloadRefEditCondition ?? {}) as Record<string, string>;
  const pu = (o.payloadRefUseCondition ?? {}) as Record<string, string>;
  return {
    id: String(o.id),
    title: o.title ?? '',
    description: o.description ?? '',
    offerNumber: o.offerNumber ?? '',
    startValidityDate: o.startValidityDate ? o.startValidityDate.slice(0, 10) : '',
    endValidityDate: o.endValidityDate ? o.endValidityDate.slice(0, 10) : '',
    nbMaxEdit: o.nbMaxEdit != null ? String(o.nbMaxEdit) : '',
    nbMaxUse: o.nbMaxUse != null ? String(o.nbMaxUse) : '',
    discountTypeIds: o.refDiscountTypeId ? [o.refDiscountTypeId] : [],
    remiseMode: (pd.remiseMode as OfferFormData['remiseMode']) || 'montant',
    remiseValue: pd.remiseValue || '',
    pointsMode: (pd.pointsMode as OfferFormData['pointsMode']) || 'ajout',
    pointsValue: pd.pointsValue || '',
    triggerId: o.refEditConditionId ? (TRIGGER_KEY_MAP[o.refEditConditionId] ?? null) : null,
    triggerMontantMin: pe.triggerMontantMin || '',
    triggerMontantMax: pe.triggerMontantMax || '',
    triggerArticleMin: pe.triggerArticleMin || '',
    triggerArticleMax: pe.triggerArticleMax || '',
    triggerPointsMin: pe.triggerPointsMin || '',
    triggerPointsMax: pe.triggerPointsMax || '',
    prereqId: o.refUseConditionId ? (PREREQ_KEY_MAP[o.refUseConditionId] ?? null) : null,
    prereqMontantMin: pu.prereqMontantMin || '',
    prereqMontantMax: pu.prereqMontantMax || '',
    prereqArticleMin: pu.prereqArticleMin || '',
    prereqArticleMax: pu.prereqArticleMax || '',
    prereqPointsMin: pu.prereqPointsMin || '',
    prereqPointsMax: pu.prereqPointsMax || '',
  };
};

export interface OfferFormData {
  id: string;
  title: string;
  offerNumber: string;
  description: string;
  startValidityDate: string;
  endValidityDate: string;
  nbMaxEdit: string;
  nbMaxUse: string;
  discountTypeIds: number[];
  remiseMode: 'montant' | 'pourcentage';
  remiseValue: string;
  pointsMode: 'ajout' | 'multiplication';
  pointsValue: string;
  triggerId: string | null;
  triggerMontantMin: string;
  triggerMontantMax: string;
  triggerArticleMin: string;
  triggerArticleMax: string;
  triggerPointsMin: string;
  triggerPointsMax: string;
  prereqId: string | null;
  prereqMontantMin: string;
  prereqMontantMax: string;
  prereqArticleMin: string;
  prereqArticleMax: string;
  prereqPointsMin: string;
  prereqPointsMax: string;
}

export const emptyOffer = (id: string): OfferFormData => ({
  id,
  title: '',
  offerNumber: '',
  description: '',
  startValidityDate: '',
  endValidityDate: '',
  nbMaxEdit: '',
  nbMaxUse: '',
  discountTypeIds: [],
  remiseMode: 'montant',
  remiseValue: '',
  pointsMode: 'ajout',
  pointsValue: '',
  triggerId: null,
  triggerMontantMin: '',
  triggerMontantMax: '',
  triggerArticleMin: '',
  triggerArticleMax: '',
  triggerPointsMin: '',
  triggerPointsMax: '',
  prereqId: null,
  prereqMontantMin: '',
  prereqMontantMax: '',
  prereqArticleMin: '',
  prereqArticleMax: '',
  prereqPointsMin: '',
  prereqPointsMax: '',
});

interface ConditionColumnsProps {
  selectedId: string | null;
  prefix: 'trigger' | 'prereq';
  offer: OfferFormData;
  onChange: (patch: Partial<OfferFormData>) => void;
}

const ConditionColumns = memo(({ selectedId, prefix, offer, onChange }: ConditionColumnsProps) => {
  const showMontant = selectedId === 'montant';
  const showArticle = selectedId === 'article';
  const showPoints  = selectedId === 'points';

  if (!showMontant && !showArticle && !showPoints) return null;

  const field = (key: keyof OfferFormData, label: string) => (
    <TextField
      fullWidth
      size="small"
      label={label}
      value={offer[key] as string}
      onChange={e => onChange({ [key]: e.target.value })}
    />
  );

  return (
    <Grid container spacing={2} sx={{ mt: 0.5 }} >
      {showMontant && (
        <Grid size={{ xs: 12, md: 4 }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" sx={{ mb: 1 }}>
            Montant de la transaction
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {field(`${prefix}MontantMin` as keyof OfferFormData, "Montant d'achat minimum")}
            {field(`${prefix}MontantMax` as keyof OfferFormData, "Montant d'achat maximum")}
          </Box>
        </Grid>
      )}
      {showArticle && (
        <Grid size={{ xs: 12, md: 4 }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" sx={{ mb: 1 }}>
            {"Nombre d'articles"}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {field(`${prefix}ArticleMin` as keyof OfferFormData, 'Quantité minimum')}
            {field(`${prefix}ArticleMax` as keyof OfferFormData, 'Quantité maximum')}
          </Box>
        </Grid>
      )}
      {showPoints && (
        <Grid size={{ xs: 12, md: 4 }}>
          <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" sx={{ mb: 1 }}>
            Nombre de points
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {field(`${prefix}PointsMin` as keyof OfferFormData, 'Nombre de points minimum')}
            {field(`${prefix}PointsMax` as keyof OfferFormData, 'Nombre de points maximum')}
          </Box>
        </Grid>
      )}
    </Grid>
  );
});

export interface OffersStepHandle {
  getOffers: () => OfferFormData[];
}

interface OffersStepProps {
  offers: OfferFormData[];
  onOffersChange: (offers: OfferFormData[]) => void;
  usedOfferIndices?: Set<number>;
}

const CommunicationOffersStep = forwardRef<OffersStepHandle, OffersStepProps>(({ offers: initialOffers, onOffersChange, usedOfferIndices }, ref) => {
  const { sidebarColor } = useLayoutPreferences();

  const [localOffers, setLocalOffers] = useState<OfferFormData[]>(initialOffers);

  useEffect(() => {
    if (initialOffers.length > localOffers.length) {
      setLocalOffers(prev => [
        ...prev,
        ...initialOffers.slice(prev.length),
      ]);
    }
  }, [initialOffers.length]);

  useImperativeHandle(ref, () => ({ getOffers: () => localOffers }), [localOffers]);

  const updateOffer = useCallback((id: string, patch: Partial<OfferFormData>) =>
    setLocalOffers(prev => prev.map(o => (o.id === id ? { ...o, ...patch } : o))), []);

  const removeOffer = useCallback((id: string) => {
    setLocalOffers(prev => {
      const updated = prev.filter(o => o.id !== id);
      onOffersChange(updated);
      return updated;
    });
  }, [onOffersChange]);

  const addOffer = useCallback(() => {
    setLocalOffers(prev => {
      const updated = [...prev, emptyOffer(String(Date.now()))];
      onOffersChange(updated);
      return updated;
    });
  }, [onOffersChange]);

  const toggleBtnSx = {
    whiteSpace: 'pre-line' as const,
    lineHeight: 1.3,
    fontSize: '0.72rem',
    px: 1.5,
    py: 0.8,
    border: '1px solid #dee2e6 !important',
    borderRadius: '8px !important',
    color: '#495057',
    textTransform: 'none' as const,
    '&.Mui-selected': {
      backgroundColor: `${sidebarColor}15`,
      color: sidebarColor,
      borderColor: `${sidebarColor} !important`,
      fontWeight: 600,
    },
    '&:hover': {
      backgroundColor: `${sidebarColor}10`,
    },
  };

  const sectionTitle = (label: string) => (
    <Typography variant="body2" fontWeight={700} sx={{ color: sidebarColor, mb: 1.5, mt: 2 }}>
      {label}
    </Typography>
  );

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      {localOffers.map((offer, idx) => (
        <Box key={offer.id}>
          {idx > 0 && <Divider sx={{ my: 3 }} />}

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              Offre {idx + 1}
            </Typography>
            {idx > 0 && (() => {
              const isUsed = usedOfferIndices?.has(idx) ?? false;
              return (
                <Tooltip title={isUsed
                  ? `Cette offre est utilisée dans le builder (code #NUM_COUPON_${idx}#) — retirez-la du contenu avant de la supprimer`
                  : 'Supprimer cette offre'
                }>
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => !isUsed && removeOffer(offer.id)}
                      disabled={isUsed}
                      sx={{ color: isUsed ? '#adb5bd' : '#dc3545' }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              );
            })()}
          </Box>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth size="small" label="Titre de l'offre"
                value={offer.title}
                onChange={e => updateOffer(offer.id, { title: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth size="small" label="Description de l'offre"
                multiline rows={4}
                value={offer.description}
                onChange={e => updateOffer(offer.id, { description: e.target.value })}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth size="small" label="Code offre"
                value={offer.offerNumber}
                onChange={e => updateOffer(offer.id, { offerNumber: e.target.value })}
                sx={{ mb: 2 }}
              />
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                Dates de validité
              </Typography>
              <Grid container spacing={1} sx={{ mt: 0.5 }}>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth size="small" type="date" label="Date de début"
                    InputLabelProps={{ shrink: true }}
                    value={offer.startValidityDate}
                    onChange={e => updateOffer(offer.id, { startValidityDate: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth size="small" type="date" label="Date de fin"
                    InputLabelProps={{ shrink: true }}
                    value={offer.endValidityDate}
                    onChange={e => updateOffer(offer.id, { endValidityDate: e.target.value })}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={1} sx={{ mt: 1 }}>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth size="small" type="number" label="Nombre maximum d'editions"
                    value={offer.nbMaxEdit}
                    onChange={e => updateOffer(offer.id, { nbMaxEdit: e.target.value })}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField
                    fullWidth size="small" type="number" label="Nombre maximum d'utilisations"
                    value={offer.nbMaxUse}
                    onChange={e => updateOffer(offer.id, { nbMaxUse: e.target.value })}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Divider sx={{ mt: 3, mb: 0.5 }} />
          {sectionTitle('Type de remise')}
          <ToggleButtonGroup
            value={offer.discountTypeIds}
            onChange={(_, newIds: number[]) => updateOffer(offer.id, { discountTypeIds: newIds })}
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}
          >
            {DISCOUNT_TYPE_OPTIONS.map(dt => (
              <ToggleButton key={dt.id} value={dt.id} sx={toggleBtnSx}>
                {dt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {offer.discountTypeIds.length > 0 && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, md: 6 }}>
                <FormLabel sx={{ fontSize: '0.8rem', fontWeight: 600, color: sidebarColor }}>
                  Remise sur le montant total de la transaction
                </FormLabel>
                <RadioGroup
                  row value={offer.remiseMode}
                  onChange={e => updateOffer(offer.id, { remiseMode: e.target.value as OfferFormData['remiseMode'] })}
                  sx={{ mt: 0.5, mb: 1 }}
                >
                  <FormControlLabel value="montant" control={<Radio size="small" />} label="En montant" />
                  <FormControlLabel value="pourcentage" control={<Radio size="small" />} label="En pourcentage" />
                </RadioGroup>
                <TextField
                  size="small" label="Valeur de la remise" type="number"
                  value={offer.remiseValue}
                  onChange={e => updateOffer(offer.id, { remiseValue: e.target.value })}
                  sx={{ width: 220 }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FormLabel sx={{ fontSize: '0.8rem', fontWeight: 600, color: sidebarColor }}>
                  Mouvements de points
                </FormLabel>
                <RadioGroup
                  row value={offer.pointsMode}
                  onChange={e => updateOffer(offer.id, { pointsMode: e.target.value as OfferFormData['pointsMode'] })}
                  sx={{ mt: 0.5, mb: 1 }}
                >
                  <FormControlLabel value="ajout" control={<Radio size="small" />} label="Ajout de points" />
                  <FormControlLabel value="multiplication" control={<Radio size="small" />} label="Multiplication selon coefficient" />
                </RadioGroup>
                <TextField
                  size="small" label="Valeur" type="number"
                  value={offer.pointsValue}
                  onChange={e => updateOffer(offer.id, { pointsValue: e.target.value })}
                  sx={{ width: 220 }}
                />
              </Grid>
            </Grid>
          )}

          <Divider sx={{ mt: 2, mb: 0.5 }} />
          {sectionTitle('Déclenchement de la remise')}
          <ToggleButtonGroup
            exclusive
            value={offer.triggerId}
            onChange={(_, val: string | null) => updateOffer(offer.id, { triggerId: val })}
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}
          >
            {TRIGGER_OPTIONS.map(opt => (
              <ToggleButton key={opt.id} value={opt.id} sx={toggleBtnSx}>
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <ConditionColumns
            selectedId={offer.triggerId}
            prefix="trigger"
            offer={offer}
            onChange={patch => updateOffer(offer.id, patch)}
          />

          <Divider sx={{ mt: 2, mb: 0.5 }} />
          {sectionTitle("Prérequis pour l'utilisation")}
          <ToggleButtonGroup
            exclusive
            value={offer.prereqId}
            onChange={(_, val: string | null) => updateOffer(offer.id, { prereqId: val })}
            sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}
          >
            {PREREQ_OPTIONS.map(opt => (
              <ToggleButton key={opt.id} value={opt.id} sx={toggleBtnSx}>
                {opt.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
          <ConditionColumns
            selectedId={offer.prereqId}
            prefix="prereq"
            offer={offer}
            onChange={patch => updateOffer(offer.id, patch)}
          />
        </Box>
      ))}

      <>
        <Divider sx={{ mt: 3, mb: 2 }} />
        <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={addOffer}
            sx={{
              backgroundColor: sidebarColor,
              textTransform: 'none',
              fontWeight: 600,
              '&:hover': { backgroundColor: sidebarColor, opacity: 0.9 },
            }}
          >
            Ajouter une offre
          </Button>
      </>
    </Box>
  );
});

CommunicationOffersStep.displayName = 'CommunicationOffersStep';

export default CommunicationOffersStep;
